import React, { createRef, useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { makeStyles, Dialog, Button, Tabs, Tab, DialogContent, TextField, Fade, Paper, Tooltip, Popper, ClickAwayListener, Snackbar } from '@material-ui/core'
import deepEqual from 'deep-equal'
import { cloneDeep } from 'lodash'

import SecurityRoleTreeView from '../SecurityRoleTreeView'
import UserRoleSelector from './UserRoleSelector'
import actions from '../../../redux/actions'
import { CREATE_ROLE, UPDATE_ROLE } from '../../../redux/data/types'
import config from '../../../config'
import { Alert } from '@material-ui/lab'
import { hasPermission } from '../../../lib/components/form/RestrictedComponent'

const useStyles = makeStyles(theme => ({
  stickyBottom: {
    bottom: 0,
    position: 'sticky',
    width: '100%',
    backgroundColor: 'white',
    display: 'flex',
    justifyContent: 'flex-end',
    paddingRight: '1rem',
    paddingTop: '0.25rem',
    paddingBottom: '0.25rem',
    zIndex: 1, // to block clicks through the container
    '& > *': {
      marginLeft: '1rem'
    }
  },
  dialogPaper: {
    overflowX: 'hidden'
  },
  tabs: {
    paddingBottom: '1rem',
  },
  generalTabFields: {
    display: 'flex',
    flexDirection: 'column',
    '& > *': {
      paddingBottom: '1rem'
    }
  },
  resetButton: {
    marginRight: 'auto'
  },
  dialogRootContent: {
    padding: 0,
    paddingTop: '0 !important',
    margin: '1rem'
  },
  clonePopper: {
    position: 'absolute !important',
    top: '4rem !important',
    left: '75% !important',
    zIndex: '2',
  },
  clonePopperItem: {
    padding: '0.5rem',
    backgroundColor: 'white',
    '&:hover': {
      filter: 'brightness(90%)',
      cursor: 'pointer'
    },
    borderRadius: '6px'
  },
  textFieldLabelRoot: {
    '& label': {
      zIndex: 0
    }
  },
  roleDialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}))

const getDefaultRole = (anyRole) => {
  const defaultRole = {
    roleName: '',
    roleDescription: '',
    roleType: 'client',
    roleSchema: anyRole.roleSchema,
    userCount: 0,
    assignedUsers: []
  }
  // disable all permissions on new schemas
  Object.keys(defaultRole.roleSchema).forEach(permission => {
    Object.keys(defaultRole.roleSchema[permission]).forEach(property => {
      defaultRole.roleSchema[permission][property] = false
    })
  })
  return defaultRole
}

const cloneRole = role => {
  const cloned = {
    roleName: role.roleName,
    roleDescription: role.roleDescription,
    roleType: 'client',
    roleSchema: cloneDeep(role.roleSchema),
    userCount: 0,
    assignedUsers: []
  }
  return cloned
}

const CloneRoleButton = props => {
  const { clientRoles, onSelect } = props
  const [isOpen, setIsOpen] = useState(false)
  const classes = useStyles()

  const handleSelect = role => {
    onSelect(role)
    setIsOpen(false)
  }

  const toggleIsOpen = () => setIsOpen(!isOpen)

  const parentRef = createRef(null)

  return <div ref={parentRef}>
    <Button variant="contained" color="primary" onClick={toggleIsOpen}>
      Clone From Existing Role
    </Button>
    <Popper className={classes.clonePopper} anchorEl={parentRef.current} placement="right" open={isOpen} transition disablePortal>
      {({ TransitionProps }) => (
        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
          <Fade {...TransitionProps} timeout={200}>
            <Paper>
              {clientRoles.map((role, i) => {
                return <Tooltip key={'clone option' + i} title={role.roleDescription} enterDelay={300}>
                  <div className={classes.clonePopperItem} onClick={() => {
                    handleSelect(role)
                  }}>
                    {role.roleName}
                  </div>
                </Tooltip>
              })}
            </Paper>
          </Fade>
        </ClickAwayListener>
      )}
    </Popper>
  </div >
}

const RoleDetailsDialog = props => {
  const { role, onClose, onSave, users, clientRoles, requestPending, requestError } = props
  const classes = useStyles()
  const [cloned, setCloned] = useState(false)
  const [editedRole, setEditedRole] = useState(role || getDefaultRole(cloneDeep(clientRoles[0])))
  const tabs = ["General", "Permissions", "Users"]
  const [tab, setTab] = useState(tabs[0])
  const [showSnackbar, setShowSnackbar] = useState(requestPending || !!requestError)
  const [waitingForRequest, setWaitingForRequest] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(true)
  const snackbarDuration = 3000

  const handleChange = updatedRole => {
    setEditedRole(updatedRole)
  }

  const isEdited = !deepEqual(editedRole, role)

  const handleEditRole = (key, value) => {
    setEditedRole({ ...editedRole, [key]: value })
  }

  const handleSave = () => {
    if (role && editedRole.assignedUsers && !editedRole.userIDS) {
      editedRole.userIDS = editedRole.assignedUsers.map(user => user.userId)
    }
    onSave({ ...editedRole })
    setWaitingForRequest(true)
  }

  const handleReset = () => {
    setEditedRole(role || getDefaultRole(cloneDeep(clientRoles[0])))
    setCloned(false)
  }

  const handleAssignNewRoleUsers = ({ userIDS }) => {
    const updatedRole = cloneDeep(editedRole)
    updatedRole.assignedUsers = userIDS.map(id => {
      return users.find(user => user.userId === id)
    })
    updatedRole.userIDS = userIDS
    setEditedRole(updatedRole)
  }

  const handleCloseSnackbar = () => {
    setShowSnackbar(false)
  }

  useEffect(() => {
    if (requestPending || requestError) {
      setShowSnackbar(true)
    }
  }, [requestPending, requestError])

  useEffect(() => {
    if (!requestPending && !requestError && waitingForRequest) {
      // request finished successfully
      setDialogOpen(false)
      // hide the dialog and wait for the duration of the snackbar success message
      const unrender = setTimeout(() => {
        onClose()
      }, snackbarDuration + 1000)
      return () => {
        clearTimeout(unrender)
      }
    }
  }, [requestPending, requestError, waitingForRequest])


  const isReadOnly = (role && role.roleID <= 100) ||
    requestPending ||
    (role && role.roleID && !hasPermission('account_securityGroups.update')) ||
    !hasPermission('account_securityGroups.read')

  let snackMessage = requestPending ? 'Saving Role' : 'Saved Role'
  let snackStatus = requestPending ? 'info' : 'success'
  if (requestError) {
    snackMessage = 'Error: ' + requestError
    snackStatus = 'error'
  }

  const SaveButtons = <div className={classes.stickyBottom}>
    <Button variant="contained" className={classes.resetButton} onClick={handleReset} disabled={!isEdited}>Reset</Button>
    <Button variant="contained" color="secondary" onClick={() => onClose()}>Cancel</Button>
    <Button variant="contained" color="primary" disabled={!isEdited || isReadOnly} onClick={handleSave}>{role === null ? 'Create' : 'Save'}</Button>
  </div>

  return <>
    <Snackbar open={showSnackbar} autoHideDuration={snackbarDuration} onClose={handleCloseSnackbar}>
      <Alert onClose={handleCloseSnackbar} severity={snackStatus} >{snackMessage}</Alert>
    </Snackbar>
    <Dialog open={dialogOpen} onClose={onClose} classes={{ paper: classes.dialogPaper }}>
      <DialogContent classes={{ root: classes.dialogRootContent }}>
        <div className={classes.roleDialogTitle}>
          <h2>{role && role.roleName ? role.roleName : 'New Role'}{isReadOnly ? ' (Read Only)' : ''}</h2>
          {(role === null && cloned === false) ? <CloneRoleButton
            clientRoles={clientRoles}
            onSelect={selectedRole => {
              setCloned(true)
              setEditedRole(cloneRole(selectedRole))
            }}
          /> : null}
        </div>
        <Tabs variant="fullWidth" className={classes.tabs} value={tab} onChange={(ev, newTab) => setTab(newTab)}>
          {tabs.map(tab => {
            return <Tab label={tab} value={tab} key={'tab:' + tab} />
          })}
        </Tabs>
        {tab === 'General' ? <div>
          <div className={classes.generalTabFields}>
            <TextField
              classes={{ root: classes.textFieldLabelRoot }}
              disabled={isReadOnly}
              readOnly={isReadOnly}
              label="Name"
              placeholder="Name"
              variant="outlined"
              required
              value={editedRole.roleName}
              onChange={ev => handleEditRole('roleName', ev.target.value)}
            />
            <TextField
              classes={{ root: classes.textFieldLabelRoot }}
              disabled={isReadOnly}
              readOnly={isReadOnly}
              multiline
              rows={4}
              rowsMax={4}
              label="Description"
              placeholder="Description"
              variant="outlined"
              required
              value={editedRole.roleDescription}
              onChange={ev => handleEditRole('roleDescription', ev.target.value)}
            />
          </div>
          {SaveButtons}
        </div> : null}
        {tab === 'Permissions' ? <div>
          <SecurityRoleTreeView role={editedRole} onChange={handleChange} />
          {SaveButtons}
        </div> : null}
        {tab === 'Users' ? <UserRoleSelector
          // use custom user assignment handling for new roles
          assignUsers={handleAssignNewRoleUsers}
          role={editedRole}
          onClose={() => onClose()}
          editing={true}
        /> : null}
      </DialogContent>
    </Dialog >
  </>
}

const mapStateToProps = state => {
  const clientID = state.data.user.clientId
  const clientRoles = state.data.securityRoles
  const users = state.data.users
  let requestPending = false
  let requestError = null
  const updateRequest = state.request?.UPDATE_ROLE
  const createRequest = state.request?.CREATE_ROLE
  let mostRecentRequest
  if (updateRequest && createRequest) {
    mostRecentRequest = updateRequest.sentAt > createRequest.sentAt ? updateRequest : createRequest
  } else {
    mostRecentRequest = updateRequest ? updateRequest : createRequest
  }
  if (mostRecentRequest && mostRecentRequest.pending) {
    requestPending = true
  } else if (mostRecentRequest && mostRecentRequest.error) {
    requestError = mostRecentRequest.error.message
  }
  return { clientID, clientRoles, users, requestPending, requestError }
}

const mapDispatchToProps = dispatch => ({
  callDispatch: func => dispatch(func),
})

const mergeProps = (stateProps, _dispatchProps, ownProps) => {
  const dispatch = func => _dispatchProps.callDispatch(func)
  const onSave = ({ roleID, userIDS, roleName, roleDescription, roleSchema }) => {
    const postData = {
      clientID: stateProps.clientID,
      roleName,
      roleDescription,
      roleSchema,
      userIDS: userIDS || []
    }
    if (roleID) {
      postData.roleID = roleID
    }
    const type = roleID ? UPDATE_ROLE : CREATE_ROLE
    const urlDomain = roleID ? 'SecurityRoles/updateSecurityRole' : 'SecurityRoles/createSecurityRole'
    dispatch(actions.request.sendRequest({
      type,
      url: `${config.uris.crud}${urlDomain}`,
      postData
    }))
  }
  return { onSave, ...stateProps, ...ownProps }
}

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(RoleDetailsDialog)
